import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { sortByParamConfig } from '@shared/models/sortby-param';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { getAssociationCandidateIds } from '@collections/associations/associations.selectors';
import { getCandidatePageKey } from '@shared/candidate-filters/candidate-filters.model';
import { Association, AssociationFilter } from '@shared/models';
import {
  AssociationService,
  CandidateAssociationService,
  JobService,
  AssociationParams,
  ApiRequest,
  CandidateAssociationParams,
} from '@shared/services';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { isTruthy } from '@shared/utils';

import { getAllJobPageCandidatesIds } from '../../modules/assigns/store/assigns.selectors';
import { getCandidatesSelectedId } from '../candidates';
import {
  associationManySuccessEffectPatchAssociationFilter,
  associationsGetList,
  associationsGetListFailure,
  associationsGetListSuccess,
  associationsLoadFailure,
  associationsLoadSelectedCandidateOnAssignsContainerPageChange,
  associationsLoadSuccess,
  associationsPatch,
  associationsPatchFailure,
  associationsPatchSuccess,
  associationsSuccessEffectLoadPatchAssociationFilter,
  associationUpdateFilterEffect,
  loadAssociationsForCandidateOnAssignToJobComponent,
  loadAssociationsForSelectedCandidateEffectPatchAssociationFilter,
  patchSelectedCandidateAssociationFilterEffect,
} from './associations.actions';
import { ApplicationHistoryService } from '@shared/services/application-history.service';

@Injectable()
export class AssociationsEffects {
  getList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associationsGetList),
      withLatestFrom(
        this.store.select(getAllJobPageCandidatesIds),
        this.store.select(getAssociationCandidateIds),
      ),
      mergeMap(
        ([
          { jobId, candidateIds, hiringManagerAction },
          pageCandidateIds,
          associationCandidateIds,
        ]) => {
          const candidatesIdsToSend = !!hiringManagerAction
            ? candidateIds
            : pageCandidateIds.filter((x) => !associationCandidateIds.includes(x));
          return this.jobService
            .getAssociationsByJobIdAndCandidateIds(jobId, candidatesIdsToSend)
            .pipe(
              map((associations) => associationsGetListSuccess({ associations })),
              catchError((error) => of(associationsGetListFailure({ error }))),
            );
        },
      ),
    ),
  );

  loadForSelectedCandidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(associationUpdateFilterEffect),
      switchMap(({ params }) =>
        this.store.select(getCandidatesSelectedId).pipe(
          first(),
          filter(isTruthy),
          map((id) => [params, id]),
        ),
      ),
      switchMap(([params, id]: [any, number]) => {
        const requestParams = this.getRequestParamsWithDefaultSort(params);
        return (
          !!id &&
          this.applicationHistoryService
            .getApplicationHistory({ id, params: { ...requestParams } })
            .pipe(
              map((response) => associationsLoadSuccess({ associationPage: response })),
              catchError((error) => of(associationsLoadFailure({ error }))),
            )
        );
      }),
    ),
  );

  associationUpdateFilterCandidateSelected = createEffect(() =>
    this.actions$.pipe(
      ofType(associationsLoadSelectedCandidateOnAssignsContainerPageChange),
      withLatestFrom(this.store.select(getCandidatesSelectedId)),
      map(([{ currentFilter }, id]) =>
        loadAssociationsForSelectedCandidateEffectPatchAssociationFilter({
          id: getCandidatePageKey(id),
          filter: currentFilter,
        }),
      ),
    ),
  );

  patchSelectedCandidateAssociationFilterForAssignedJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        associationManySuccessEffectPatchAssociationFilter,
        loadAssociationsForSelectedCandidateEffectPatchAssociationFilter,
        associationsSuccessEffectLoadPatchAssociationFilter,
      ),
      map((params) => this.refreshAssociationFilterByLocalStorage(params)),
      map((newFilter) => associationUpdateFilterEffect({ params: newFilter })),
    ),
  );

  patchSelectedCandidateAssociationFilterForAllJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patchSelectedCandidateAssociationFilterEffect),
      map((params) => {
        const associationFilter = this.refreshAssociationFilterByLocalStorage(params);
        return { ...associationFilter, assigned: params.assigned };
      }),
      map((associationParams: AssociationParams) =>
        associationUpdateFilterEffect({ params: associationParams }),
      ),
    ),
  );

  private refreshAssociationFilterByLocalStorage(params): AssociationFilter {
    const oldFilter = this.localStorage.getSavedState<object>(params.id);
    let newFilter;
    if (params.filter) {
      newFilter = {
        ...oldFilter,
        association: params.filter,
      };
    } else {
      newFilter = oldFilter;
    }

    this.localStorage.setSavedState(newFilter, params.id);
    return newFilter.association;
  }

  loadForCandidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAssociationsForCandidateOnAssignToJobComponent),
      switchMap(({ candidateId }) => {
        const requestParams = this.getRequestParamsWithDefaultSort();
        return this.candidateService
          .get({ id: candidateId, params: { ...requestParams, assigned: true } })
          .pipe(
            map((response) => associationsLoadSuccess({ associationPage: response })),
            catchError((error) => of(associationsLoadFailure({ error }))),
          );
      }),
    ),
  );

  patch = createEffect(() =>
    this.actions$.pipe(
      ofType(associationsPatch),
      switchMap(({ association }) =>
        this.associationService.patch({
          id: association.id,
          body: association,
        }),
      ),
      map((association: Association) => associationsPatchSuccess({ association })),
      catchError((error) => of(associationsPatchFailure({ error }))),
    ),
  );

  private getRequestParamsWithDefaultSort(
    params?: Partial<ApiRequest<CandidateAssociationParams>>,
  ) {
    const defaultParams = {
      'sort.by': sortByParamConfig.latestApplicationDate.sortBy,
      'sort.direction': sortByParamConfig.latestApplicationDate.direction,
    };

    return !!params
      ? {
          ...defaultParams,
          ...params,
        }
      : defaultParams;
  }

  constructor(
    private actions$: Actions,
    private store: Store<{}>,
    private jobService: JobService,
    private associationService: AssociationService,
    private candidateService: CandidateAssociationService,
    private localStorage: LocalStorageService,
    private applicationHistoryService: ApplicationHistoryService,
  ) {}
}
